import React from "react";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { assetUrl } from "../lib/api";

/**
 * BUFL / WUFL logo. When the admin has uploaded a logo via /admin/settings,
 * we render that image; otherwise we fall back to the typographic placeholder
 * which is the strict pre-Phase-8 look (so removing the CMS logo restores
 * the original design without any code change).
 *
 * `variant="header"` picks logo_white_url first (dark header), then main;
 * `variant="footer"` picks logo_white_url first (dark footer), then main.
 * Setting a dedicated dark asset for light-bg contexts is available but no
 * public page uses it yet.
 */
export const BuflLogo = ({ compact = false, className = "", variant = "header", ...rest }) => {
  const { settings } = useSiteSettings();
  const preferred =
    variant === "header" || variant === "footer"
      ? settings?.logo_white_url || settings?.logo_main_url
      : settings?.logo_dark_url || settings?.logo_main_url;

  if (preferred) {
    return (
      <span
        className={`inline-flex items-center gap-2 ${className}`}
        aria-label="BUFL"
        data-testid="bufl-logo-img"
        {...rest}
      >
        <img
          src={assetUrl(preferred)}
          alt="BUFL"
          loading="eager"
          className={compact ? "h-7 md:h-8 w-auto" : "h-8 md:h-10 w-auto"}
        />
      </span>
    );
  }

  // Fallback typographic placeholder (unchanged from Phase 1).
  return (
    <span
      className={`inline-flex items-baseline gap-1 font-display font-black tracking-tighter ${className}`}
      style={{ letterSpacing: "-0.04em" }}
      aria-label="BUFL"
      data-testid="bufl-logo-typo"
      {...rest}
    >
      <span
        className="text-2xl md:text-3xl leading-none"
        style={{
          backgroundImage:
            "linear-gradient(180deg, #f5f5f5 0%, #a3a3a3 60%, #6b6b6b 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "#f5f5f5",
        }}
      >
        BUFL
      </span>
      {!compact && (
        <span className="text-[10px] md:text-xs text-neutral-500 tracking-widest ml-1 hidden sm:inline">
          BURKINA UNITED FIGHT LEAGUE
        </span>
      )}
    </span>
  );
};

export const WuflLogo = ({ className = "" }) => (
  <span
    className={`inline-flex items-baseline gap-1 font-display font-black tracking-tighter ${className}`}
    aria-label="WUFL"
  >
    <span
      className="text-xl leading-none"
      style={{
        backgroundImage:
          "linear-gradient(180deg, #ececec 0%, #b7b7b7 60%, #ef3340 120%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "#ececec",
      }}
    >
      WUFL
    </span>
  </span>
);

export default BuflLogo;
